/* ─── Blog Posts Data ───
 *
 * Loads blog posts from JSON files in src/content/blog/{hr,en,sl,de,it}/.
 *
 * Strategy: HR is the canonical source (BookiApp's primary market is Croatia).
 * Other languages are translations targeting *foreign owners renting in
 * Croatia* — Slovenes, Germans/Austrians, Italians, English-speakers — so the
 * legal/tax context stays Croatian (eVisitor, paušalni porez, kategorizacija)
 * but the language is localised for the reader.
 *
 * Generator scripts write new posts directly to those folders; this module
 * stitches each language pair into the BlogPost shape that BlogPage and
 * BlogPostPage already consume.
 *
 * The legacy BlogPost interface kept `titleEn`/`contentEn` for the original
 * HR/EN-only world. We retain those for backwards compat but the new
 * `getPostByLang()` / `getAllPostsByLang()` helpers are the multi-lang API.
 */

import type { BlogLang } from './blogLangs';
import { SUPPORTED_LANGS } from './blogLangs';

// Languages live in ./blogLangs (the app shell uses them without this module);
// re-exported for existing imports.
export { SUPPORTED_LANGS };
export type { BlogLang };

export interface BlogAuthor {
  name: string;
  role: string;
  roleEn: string;
  bio: string;
  bioEn: string;
  avatar: string;
}

export interface BlogFAQ {
  question: string;
  questionEn: string;
  answer: string;
  answerEn: string;
}

/** Legacy bilingual shape used by existing components — kept for compat. */
export interface BlogPost {
  slug: string;
  title: string;
  titleEn: string;
  description: string;
  descriptionEn: string;
  tldr: string;
  tldrEn: string;
  category: string;
  categoryEn: string;
  categoryColor: string;
  date: string;
  /** ISO date when content was last reviewed/refreshed — drives AI freshness signals.
   * Defaults to `date` when not present. */
  lastReviewed?: string;
  readTime: number;
  author: BlogAuthor;
  content: string;
  contentEn: string;
  faq: BlogFAQ[];
  coverImage: string;
  coverImageAlt: string;
  inlineImages?: string[];
  /** Long-tail keywords for the article — feeds Article.keywords schema. */
  keywords?: string[];
  /** Concrete entities the article is about — feeds Article.about schema. */
  about?: string[];
}

/** Per-language localized post — what JSON files actually contain. */
export interface LocalizedPost {
  slug: string;
  title: string;
  description: string;
  tldr: string;
  category: string;
  categoryColor: string;
  date: string;
  lastReviewed?: string;
  readTime: number;
  author: {
    name: string;
    role: string;
    bio: string;
    avatar: string;
  };
  content: string;
  faq: { question: string; answer: string }[];
  coverImage: string;
  coverImageAlt: string;
  lang: BlogLang;
  inlineImages?: string[];
  keywords?: string[];
  about?: string[];
  /** 3-5 scannable bullet points rendered above TL;DR. AEO win — LLMs cite lists 1:1. */
  keyTakeaways?: string[];
  /** Authoritative citations rendered as a numbered "Izvori" section at article bottom.
   *  In markdown, reference these with [^1], [^2] footnote-style markers. */
  sources?: { label: string; url: string }[];
  /** When true, BlogPostPage emits HowTo JSON-LD derived from numbered list steps.
   *  Use for step-by-step articles ("Kako prijaviti...", "Kako instalirati..."). */
  isHowTo?: boolean;
}

export const defaultAuthor: BlogAuthor = {
  name: 'BookiApp Tim',
  role: 'Aplikacija za goste smještaja',
  roleEn: 'Guest experience app for short-term rentals',
  bio: 'BookiApp Tim okuplja iskustvo aktivnih iznajmljivača i analitičke uvide iz tržišta. Pišemo praktične vodiče za male iznajmljivače apartmana, vila i soba — bez floskula, s konkretnim brojkama i provjerenim izvorima.',
  bioEn: 'BookiApp Team combines hands-on experience from active hosts with market analytics. We publish practical guides for small vacation rental hosts — no fluff, just concrete numbers and verified sources.',
  avatar: 'BA',
};

// Vite's import.meta.glob with eager: true inlines the JSON at build time,
// so the bundle ships posts as a static object — no runtime fetch.
const hrModules = import.meta.glob<LocalizedPost>('../content/blog/hr/*.json', { eager: true, import: 'default' });
const enModules = import.meta.glob<LocalizedPost>('../content/blog/en/*.json', { eager: true, import: 'default' });
const slModules = import.meta.glob<LocalizedPost>('../content/blog/sl/*.json', { eager: true, import: 'default' });
const deModules = import.meta.glob<LocalizedPost>('../content/blog/de/*.json', { eager: true, import: 'default' });
const itModules = import.meta.glob<LocalizedPost>('../content/blog/it/*.json', { eager: true, import: 'default' });

function indexBySlug(modules: Record<string, LocalizedPost>): Record<string, LocalizedPost> {
  const out: Record<string, LocalizedPost> = {};
  for (const post of Object.values(modules)) {
    if (post && typeof post === 'object' && 'slug' in post) {
      out[post.slug] = post;
    }
  }
  return out;
}

const POSTS_BY_LANG: Record<BlogLang, Record<string, LocalizedPost>> = {
  hr: indexBySlug(hrModules),
  en: indexBySlug(enModules),
  sl: indexBySlug(slModules),
  de: indexBySlug(deModules),
  it: indexBySlug(itModules),
};

// ─── New per-language API (preferred for new code) ──────────────

/**
 * Returns posts for a specific language, sorted newest-first.
 * Slugs are unified across languages (same slug = same article in another lang).
 */
export function getAllPostsByLang(lang: BlogLang): LocalizedPost[] {
  return Object.values(POSTS_BY_LANG[lang]).sort((a, b) => b.date.localeCompare(a.date));
}

export function getPostByLang(slug: string, lang: BlogLang): LocalizedPost | undefined {
  return POSTS_BY_LANG[lang][slug];
}

/**
 * Returns the languages a given slug is available in.
 * Used to render hreflang alternates only for languages we actually have.
 */
export function getAvailableLangs(slug: string): BlogLang[] {
  return SUPPORTED_LANGS.filter((lang) => POSTS_BY_LANG[lang][slug] !== undefined);
}

// ─── Legacy bilingual API (kept for existing component compat) ──

function buildBilingualPost(slug: string): BlogPost | null {
  const hr = POSTS_BY_LANG.hr[slug];
  if (!hr) return null;

  // Falls back to the HR copy when an EN translation isn't available yet.
  const en = POSTS_BY_LANG.en[slug];

  return {
    slug: hr.slug,
    title: hr.title,
    titleEn: en?.title ?? hr.title,
    description: hr.description,
    descriptionEn: en?.description ?? hr.description,
    tldr: hr.tldr,
    tldrEn: en?.tldr ?? hr.tldr,
    category: hr.category,
    categoryEn: en?.category ?? hr.category,
    categoryColor: hr.categoryColor,
    date: hr.date,
    readTime: hr.readTime,
    author: {
      name: hr.author.name,
      role: hr.author.role,
      roleEn: en?.author.role ?? defaultAuthor.roleEn,
      bio: hr.author.bio,
      bioEn: en?.author.bio ?? defaultAuthor.bioEn,
      avatar: hr.author.avatar,
    },
    content: hr.content,
    contentEn: en?.content ?? hr.content,
    faq: hr.faq.map((f, i) => ({
      question: f.question,
      questionEn: en?.faq?.[i]?.question ?? f.question,
      answer: f.answer,
      answerEn: en?.faq?.[i]?.answer ?? f.answer,
    })),
    coverImage: hr.coverImage,
    coverImageAlt: hr.coverImageAlt,
    inlineImages: hr.inlineImages,
    lastReviewed: hr.lastReviewed,
    keywords: hr.keywords,
    about: hr.about,
  };
}

// Newest first — matches the previous hard-coded order.
export const blogPosts: BlogPost[] = Object.keys(POSTS_BY_LANG.hr)
  .map(buildBilingualPost)
  .filter((p): p is BlogPost => p !== null)
  .sort((a, b) => b.date.localeCompare(a.date));

export function getBlogPost(slug: string): BlogPost | undefined {
  return blogPosts.find((post) => post.slug === slug);
}

export function getAllBlogPosts(): BlogPost[] {
  return blogPosts;
}
